import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { AppSelect } from '../../../components/AppSelect';
import { formatMoney } from '../../../utils/formatMoney';
import type { DashboardCategorySlice, DashboardPipelineOverview } from '../types';
import { DashboardCategoryDonut } from './DashboardCategoryDonut';
import { DashboardPipelineRow } from './DashboardPipelineRow';

type DashboardPipelinePanelProps = {
  pipeline: DashboardPipelineOverview;
  categories: DashboardCategorySlice[];
};

export function DashboardPipelinePanel({ pipeline, categories }: DashboardPipelinePanelProps) {
  const { instanceId } = useParams<{ instanceId: string }>();
  const pipelineOptions = pipeline.pipelines;
  const hasMultiplePipelines = pipelineOptions.length > 1;
  const [selectedPipelineId, setSelectedPipelineId] = React.useState(pipeline.defaultPipelineId);

  React.useEffect(() => {
    const stillExists = pipelineOptions.some((p) => p.id === selectedPipelineId);
    if (!stillExists) setSelectedPipelineId(pipeline.defaultPipelineId);
  }, [pipeline.defaultPipelineId, pipelineOptions, selectedPipelineId]);

  const breakdown =
    pipeline.byPipeline.find((b) => b.pipelineId === selectedPipelineId) ?? pipeline.byPipeline[0] ?? null;
  const phases = breakdown?.phases ?? [];
  const maxCount = phases.reduce((max, p) => Math.max(max, p.count), 0);
  const hasPhases = phases.length > 0;
  const won = breakdown?.won ?? { count: 0, angebotsvolumen: 0 };
  const lost = breakdown?.lost ?? { count: 0, angebotsvolumen: 0 };

  return (
    <div className="rounded-xl border border-app-border bg-app-surface p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-sm font-semibold text-app-text">Pipeline nach Phase</h2>
        <div className="flex items-center gap-3">
          {hasMultiplePipelines ? (
            <AppSelect
              value={selectedPipelineId}
              onChange={setSelectedPipelineId}
              options={pipelineOptions.map((p) => ({ value: p.id, label: p.name }))}
              size="sm"
              className="w-36"
              // Gleich hoch wie Überschrift und Link; der Standard-Trigger ist 40 px hoch.
              triggerClassName="!h-8 !min-h-0 !py-0 !text-xs !rounded-md"
            />
          ) : null}
          <Link
            to={`/i/${encodeURIComponent(instanceId ?? 'default')}/projects/pipeline`}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
          >
            Alle Anfragen
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.6fr_1fr] lg:gap-6">
        <div>
          <div className="grid grid-cols-[minmax(0,5.25rem)_1fr_1.75rem_4.5rem] items-center gap-x-2 gap-y-2 text-[11px] sm:grid-cols-[minmax(0,9rem)_1fr_2.25rem_6.5rem] sm:gap-x-3 sm:gap-y-2.5 sm:text-xs">
            {hasPhases ? (
              phases.map((phase) => (
                <DashboardPipelineRow key={phase.status} phase={phase} maxCount={maxCount} />
              ))
            ) : (
              <p className="col-span-4 text-xs text-slate-400">Noch keine Anfragen in der Pipeline.</p>
            )}
          </div>
          <div className="mt-4 flex items-center gap-4 border-t border-app-border pt-3 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <span className="font-medium text-app-text-muted">Gewonnen</span>
              <span className="font-semibold text-app-text tabular-nums">{won.count}</span>
              <span className="text-slate-400 tabular-nums">{formatMoney(won.angebotsvolumen)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-rose-400" />
              <span className="font-medium text-app-text-muted">Verloren</span>
              <span className="font-semibold text-app-text tabular-nums">{lost.count}</span>
              <span className="text-slate-400 tabular-nums">{formatMoney(lost.angebotsvolumen)}</span>
            </div>
          </div>
        </div>
        <div className="border-t border-app-border pt-4 lg:border-t-0 lg:border-l lg:pl-6 lg:pt-0">
          <DashboardCategoryDonut categories={categories} />
        </div>
      </div>
    </div>
  );
}
